import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import SEO from '../components/SEO';
import Schema, { createArticleSchema } from '../components/Schema';
import axios from 'axios';

const BlogArticle = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center">Post not found</div>;

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={post.title}
        description={post.excerpt || 'Read the latest insights on SAP Business One and ERP solutions from Ascarii.'}
        keywords={`SAP Business One, ${post.category || 'ERP'}, blog, ${post.tags?.join(', ') || 'insights'}`}
        ogType="article"
        canonical={`https://www.ascarii.com/blog/${post.slug}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title }
        ]}
      />
      <Schema data={createArticleSchema({
        title: post.title,
        description: post.excerpt,
        author: post.author,
        publishedDate: post.published_at,
        modifiedDate: post.updated_at,
        image: post.featured_image?.startsWith('http') ? post.featured_image : `https://www.ascarii.com${post.featured_image}`,
        category: post.category,
        url: `https://www.ascarii.com/blog/${post.slug}`,
        wordCount: post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : undefined
      })} />
      <article className="py-20">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Blog
          </Link>

          {/* Featured Image */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 rounded-2xl overflow-hidden">
            <img src={post.featured_image.startsWith('http') ? post.featured_image : `${BACKEND_URL}${post.featured_image}`} alt={post.title} className="w-full h-96 object-cover" />
          </motion.div>

          {/* Article Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8">
            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center"><Calendar className="w-4 h-4 mr-2" />{new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span>•</span>
              <span className="flex items-center"><User className="w-4 h-4 mr-2" />{post.author}</span>
              <span>•</span>
              <span className="flex items-center"><Tag className="w-4 h-4 mr-2" />{post.category}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">{post.title}</h1>
            <p className="text-xl text-gray-600 leading-relaxed">{post.excerpt}</p>
          </motion.div>

          {/* Article Content */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="prose prose-lg max-w-none">
            <div className="prose prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: post.content }} />
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;
